import type { ReactNode } from 'react'
import { Banner, Button, LayerCard, Text } from '@cloudflare/kumo'
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'
import { StickyActionBar } from './StickyActionBar'
import './enroll.css'

interface PhotoConfirmProps {
  error: string | null
  previewUrl: string | null
  /** Optional summary rows shown under the photo (label / value). */
  rows?: { label: string; value: ReactNode }[]
  onRetake: () => void
  onSubmit: () => void
  submitting: boolean
  canSubmit: boolean
}

/** Confirm step body: summary card (selfie + details), Retake, the LGPD
 * notice, and the sticky "Agree and submit" button (tapping it is the
 * consent). Shared by both enrollment pages. */
export function PhotoConfirm({
  error,
  previewUrl,
  rows,
  onRetake,
  onSubmit,
  submitting,
  canSubmit,
}: PhotoConfirmProps) {
  const { t } = useTranslation()
  return (
    <div data-testid="enroll-step-confirm" className="flex flex-col gap-4">
      {error && <Banner variant="error">{error}</Banner>}

      <LayerCard>
        <LayerCard.Primary className="flex flex-col items-center gap-4">
          {previewUrl && (
            <img src={previewUrl} alt={t('enroll.step3.previewAlt')} className="enroll-preview" />
          )}
          <Button
            variant="secondary"
            icon={<ArrowCounterClockwiseIcon size={16} />}
            onClick={onRetake}
            className="w-full"
          >
            {t('enroll.step3.retake')}
          </Button>
          {rows && rows.length > 0 && (
            <dl className="flex w-full flex-col gap-2">
              {rows.map((row) => (
                <div key={row.label} className="flex flex-col">
                  <dt>
                    <Text variant="secondary" size="sm">
                      {row.label}
                    </Text>
                  </dt>
                  <dd>
                    <Text>{row.value}</Text>
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </LayerCard.Primary>
      </LayerCard>

      <Text variant="secondary" size="sm">
        {t('enroll.step3.consentText')}
      </Text>

      <StickyActionBar>
        <Button
          variant="primary"
          loading={submitting}
          disabled={!canSubmit || submitting}
          onClick={onSubmit}
          className="enroll-cta w-full"
        >
          {t('enroll.step3.submit')}
        </Button>
      </StickyActionBar>
    </div>
  )
}
